import { Switch, Route, Router, useLocation } from "wouter";
import { useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "./components/ThemeProvider";
import { LanguageProvider } from "./contexts/LanguageContext";
import Home from "@/pages/Home";
import AllServices from "@/pages/AllServices";
import AllProjects from "@/pages/AllProjects";
import About from "@/pages/About";
import ProjectDetail from "@/pages/ProjectDetail";
import NotFound from "@/pages/not-found";
import Seo from "@/components/Seo";
import { DEFAULT_LANGUAGE, getPagePath, getPathLanguage, localizedPath, normalizePath } from "@/lib/site";
import { pagePaths } from "@/lib/seo";

function Pages() {
  const [location] = useLocation();
  return (
    // Keep prerendered content visible before hydration, including motion elements.
    <AnimatePresence initial={false}>
      <div key={location}>
        <Seo />
        <Switch>
          <Route path="/" component={Home} />
          <Route path="/services" component={AllServices} />
          <Route path="/projects" component={AllProjects} />
          <Route path="/projects/:id" component={ProjectDetail} />
          <Route path="/about" component={About} />
          <Route component={NotFound} />
        </Switch>
      </div>
    </AnimatePresence>
  );
}

function LocalizedApp() {
  const [path, navigate] = useLocation();
  const pathLanguage = getPathLanguage(path);
  const language = pathLanguage || DEFAULT_LANGUAGE;
  useEffect(() => {
    if (pathLanguage && path !== normalizePath(path)) {
      navigate(normalizePath(path) + window.location.search + window.location.hash, { replace: true });
      return;
    }
    if (!pathLanguage && pagePaths.includes(getPagePath(path))) {
      navigate(localizedPath(DEFAULT_LANGUAGE, path) + window.location.search + window.location.hash, { replace: true });
    }
  }, [path, pathLanguage, navigate]);
  return (
    <ThemeProvider>
      <LanguageProvider language={language} onLanguageChange={nextLanguage => {
        navigate(localizedPath(nextLanguage, getPagePath(path)) + window.location.search + window.location.hash);
      }}>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Router base={pathLanguage ? `/${language}` : ""} hrefs={normalizePath}>
              {pathLanguage ? <Pages /> : <><Seo /><NotFound /></>}
            </Router>
          </TooltipProvider>
        </QueryClientProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

function App({ ssrPath }: { ssrPath?: string }) {
  return <Router ssrPath={ssrPath}><LocalizedApp /></Router>;
}

export default App;
